import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import { InputField } from '../../../components/ui/Input';
import { CaretDownIcon, CaretRightIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import Loading from 'src/components/ui/Loading';
import useIsMobile from 'src/hooks/useIsMobile';

interface WikiFilterDropdownOption {
  label: string;
  value: string;
  icon?: React.ReactNode;
  count?: string;
  disabled?: boolean;
}

interface WikiFilterDropdownProps {
  options: WikiFilterDropdownOption[];
  value: string[];
  onChange: (value: string[]) => void;
  label: string;
  icon?: React.ReactNode;
  placeholder?: string;
  className?: string;
  searchable?: boolean;
  initialOpen?: boolean;
  isLoading?: boolean;
  /**
   * When true, the dropdown menu floats above surrounding content instead of
   * expanding inline and pushing other elements down. Also closes on outside
   * click. Defaults to false to preserve existing sidebar-style usage.
   */
  overlay?: boolean;
  /**
   * When true, disables selection of any option. The trigger and menu still
   * render so the user can see the currently selected values, but toggling
   * checkboxes becomes a no-op. Individual options can still be disabled via
   * `options[].disabled`.
   */
  disabled?: boolean;
  /**
   * When true, renders only the options list with no trigger header, label, or
   * outer border. Intended for use inside an external container like a Popover.
   */
  bare?: boolean;
}

/**
 * WikiFilterDropdown - custom dropdown with multi-select, always-visible search (optional), checkboxes, and custom trigger
 * Figma: https://www.figma.com/design/ugGm5g9KsyFKHsw0YyToO3/INNEX-Design-System?node-id=920-4624
 */
const WikiFilterDropdown: React.FC<WikiFilterDropdownProps> = ({
  options,
  value,
  onChange,
  label,
  icon,
  placeholder = 'Select...',
  className = '',
  searchable = true,
  initialOpen = undefined,
  isLoading = false,
  overlay = false,
  disabled = false,
  bare = false
}) => {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(initialOpen ?? (!isMobile && !overlay));
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Filter options by search
  const filteredOptions = useMemo(() => {
    const s = debouncedSearch.trim().toLowerCase();
    if (!s) return options;
    return options.filter((opt) => opt.label.toLowerCase().includes(s));
  }, [debouncedSearch, options]);

  // Open/close logic
  const openMenu = () => {
    setIsOpen(true);
  };
  const closeMenu = () => setIsOpen(false);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    function handle(e: KeyboardEvent) {
      if (e.key === 'Escape') closeMenu();
    }
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [isOpen]);

  // Close overlay menu when clicking outside the component.
  useEffect(() => {
    if (!overlay || !isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeMenu();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [overlay, isOpen]);

  // Handle option toggle
  const handleOptionToggle = (optionValue: string) => {
    if (disabled) return;
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const menu = isOpen ? (
    <div
      className={`flex flex-col gap-4 ${overlay ? 'p-3 bg-white border-2 border-border-default rounded-md shadow-lg' : 'pl-3 px-2.5 pb-2.5 pt-0'}`}
      role="listbox"
    >
      {searchable && (
        <InputField
          icon={MagnifyingGlassIcon}
          placeholder={placeholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          wrapperClassName="border-border-subtle text-content-subtle"
        />
      )}
      <div className="max-h-[140px] min-h-8 overflow-y-auto flex flex-col gap-2 pr-1 custom-scrollbar-thin">
        {isLoading ? (
          <Loading text="Loading..." />
        ) : filteredOptions.length === 0 ? (
          <div className="text-content-subtle text-sm px-2 py-4 text-center">No options found</div>
        ) : (
          filteredOptions.map((opt) => {
            const isDisabled = disabled || opt.disabled;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => !isDisabled && handleOptionToggle(opt.value)}
                className={`${isDisabled ? 'cursor-not-allowed opacity-50' : ''} flex items-center w-full transition text-left gap-2 text-sm text-content-subtle`}
                tabIndex={0}
                role="option"
                aria-selected={value.includes(opt.value)}
                aria-disabled={isDisabled}
              >
                <Checkbox
                  disabled={isDisabled}
                  checked={value.includes(opt.value)}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                  className="pointer-events-none"
                />
                {opt.icon && <span className="w-4 h-4 flex items-center justify-center">{opt.icon}</span>}
                <span className={`flex-1 truncate ${value.includes(opt.value)}`}>{opt.label}</span>
                {opt.count && (
                  <span className={`${isDisabled ? '' : 'text-content-subtlest'} ml-2 text-xs  truncate`}>
                    {opt.disabled ? 0 : opt.count}
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  ) : null;

  if (bare) {
    return <div className={`flex flex-col gap-4 ${className}`}>{menu}</div>;
  }

  const trigger = (
    <div
      tabIndex={0}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      className={
        overlay
          ? `flex h-[36px] w-full items-center gap-2 rounded-lg border-2 border-border-default bg-white px-2.5 font-raleway text-sm text-content-subtle transition cursor-pointer select-none ${
              isOpen ? 'border-border-active bg-surface-hover' : 'hover:bg-surface-hover'
            }`
          : `flex items-center gap-2 px-3 py-2.5 font-raleway rounded-md text-sm transition cursor-pointer select-none text-content-subtle ${
              isOpen ? '' : 'hover:bg-blue-50'
            }`
      }
      onClick={() => (isOpen ? closeMenu() : openMenu())}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          isOpen ? closeMenu() : openMenu();
        }
      }}
    >
      {icon && <span className="flex shrink-0">{icon}</span>}
      <span className="min-w-0 flex-1 truncate" title={label}>
        {label}
      </span>
      <span className="ml-auto flex shrink-0 items-center">
        {overlay ? (
          <CaretDownIcon
            size={16}
            className={`text-content-subtle transition-transform ${isOpen ? '' : '-rotate-90'}`}
          />
        ) : isOpen ? (
          <CaretDownIcon size={12} className="text-content-subtle" />
        ) : (
          <CaretRightIcon size={12} className="text-content-subtle" />
        )}
      </span>
    </div>
  );

  if (overlay) {
    return (
      <div ref={containerRef} className={`relative ${className}`}>
        {trigger}
        {isOpen && <div className="absolute left-0 top-full z-30 mt-1 w-full min-w-0 max-w-full">{menu}</div>}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className={`flex flex-col border-2 border-border-default rounded-md bg-white w-full`}>
        {trigger}
        {menu}
      </div>
    </div>
  );
};

export default WikiFilterDropdown;
